'use client'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

export default function PrivacyPolicyPage() {
  return (
    <Container maxWidth='lg'>
      <Box py={4}>
        <Typography variant='h4' component='h1' gutterBottom>
          Privacy Policy
        </Typography>

        <Typography variant='body2' color='text.secondary' gutterBottom>
          Effective Date: 1st September 2025
        </Typography>

        <Typography variant='body2' color='text.secondary' gutterBottom>
          Platform: Polar Tensor | Trading Engine: Polar One
        </Typography>

        <Typography variant='body2' color='text.secondary' gutterBottom>
          Governing Law: Republic of Panama, US federal & Montana law, GDPR, Hong
          Kong law
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant='body1' paragraph>
          This Privacy Policy (&quot;Policy&quot;) explains how POLAR TENSOR CORP.,
          its affiliated entities, and its service providers collect, use, store,
          share, and protect personal information of users (&quot;Client&quot;,
          &quot;you&quot;, &quot;your&quot;) when interacting with the Polar Tensor
          platform and the Polar One trading system.
        </Typography>

        <Typography variant='body1' paragraph>
          By using the Platform, the Client acknowledges and accepts this Policy.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              1. Who We Are (Data Controllers & Processors)
            </Typography>
            <Typography variant='body1' paragraph>
              <strong>POLAR TENSOR CORP. (Panama):</strong> Data controller for
              investment and trading data.
            </Typography>
            <Typography variant='body1' paragraph>
              <strong>POLAR MSB INC. (Montana, USA):</strong> Data controller for
              payment processing, KYC/AML checks, and US compliance.
            </Typography>
            <Typography variant='body1' paragraph>
              <strong>POLAR TENSOR LAB LIMITED (Hong Kong):</strong> Data processor
              for platform hosting, software development, and technical maintenance.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              2. Information We Collect
            </Typography>
            <Typography variant='body1'>We collect:</Typography>
            <Box component='ul' pl={3}>
              <Typography component='li' variant='body1'>
                <strong>Identity Data:</strong> Name, DOB, nationality, ID documents,
                proof of address, tax IDs
              </Typography>
              <Typography component='li' variant='body1'>
                <strong>Compliance Data:</strong> Sanctions checks, PEP screenings,
                blockchain analytics, source of funds
              </Typography>
              <Typography component='li' variant='body1'>
                <strong>Financial Data:</strong> Deposits, withdrawals, wallet
                addresses, balances, fees, ledger entries
              </Typography>
              <Typography component='li' variant='body1'>
                <strong>Technical Data:</strong> IP address, device identifiers,
                browser info, activity logs, cookies
              </Typography>
              <Typography component='li' variant='body1'>
                <strong>Communication Data:</strong> Support tickets, emails, chat
                logs, submitted documents
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              3. How We Use Your Data
            </Typography>
            <Typography variant='body1' paragraph>
              To provide services (operate account, enable transactions, process
              payments, provide trading), comply with legal obligations (AML/KYC,
              sanctions, fraud prevention, reporting), improve the platform (optimize
              models, analyze usage, troubleshooting), protect the business (detect
              security incidents, respond to regulators, enforce terms), and
              communicate (account notifications, system updates, security alerts).
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              4. Legal Basis for Processing (GDPR Compliance)
            </Typography>
            <Typography variant='body1' paragraph>
              For EU and EEA users (accessing via reverse solicitation), data is
              processed under the following legal bases:
            </Typography>
            <Box component='ul' pl={3}>
              <Typography component='li' variant='body1'>
                <strong>Contractual Necessity:</strong> To fulfill our contractual
                obligations (e.g., trading, payments)
              </Typography>
              <Typography component='li' variant='body1'>
                <strong>Legitimate Interest:</strong> Fraud prevention, system
                optimization, security
              </Typography>
              <Typography component='li' variant='body1'>
                <strong>Legal Obligation:</strong> AML, sanctions checks, regulatory
                compliance
              </Typography>
              <Typography component='li' variant='body1'>
                <strong>Consent:</strong> For certain optional functions (e.g.,
                cookies)
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              5. Data Sharing
            </Typography>
            <Typography variant='body1' paragraph>
              We may share data with affiliates, third-party service providers (KYC
              verification, blockchain analytics, custodians, payment processors,
              cloud hosting, legal consultants, security auditors), legal &
              regulatory authorities (AML/CTF compliance, subpoenas, sanctions
              enforcement), and as part of business operations (reorganization,
              merger, acquisition).
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              6. International Data Transfers
            </Typography>
            <Typography variant='body1' paragraph>
              Data may be transferred to Panama, United States, Hong Kong, European
              Union, and global cloud hosting locations. Transfers comply with GDPR
              adequacy principles, Standard Contractual Clauses (SCCs), and local
              laws.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              7. Data Retention
            </Typography>
            <Typography variant='body1' paragraph>
              We retain data only as long as necessary for regulatory compliance,
              contractual obligations, legal defense, AML monitoring, and tax/audit
              requirements.
            </Typography>
            <Typography variant='body1' paragraph>
              In most cases:
            </Typography>
            <Box component='ul' pl={3}>
              <Typography component='li' variant='body1'>
                KYC records: 5-10 years
              </Typography>
              <Typography component='li' variant='body1'>
                Transaction records: 5-7 years
              </Typography>
              <Typography component='li' variant='body1'>
                Support communication: 2-5 years
              </Typography>
              <Typography component='li' variant='body1'>
                General account data: until closure, then archived per law
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              8. Data Security
            </Typography>
            <Typography variant='body1' paragraph>
              We implement encryption in transit and at rest, multi-factor
              authentication, firewalls and intrusion detection, segregated access
              control, secure data centers, and ongoing security audits. However, no
              system is fully secure. The Client accepts risk of cyberattacks and
              data breaches.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              9. Client Rights (GDPR Applicable)
            </Typography>
            <Typography variant='body1' paragraph>
              Clients may request access, correction, deletion (where legally
              allowed), restriction of processing, data portability, objection to
              processing, and withdrawal of consent. Requests may be denied for
              AML/CTF obligations, fraud prevention, legal compliance, or contractual
              requirements.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              10. Cookies & Tracking
            </Typography>
            <Typography variant='body1' paragraph>
              We may use session cookies, authentication cookies, analytics cookies,
              and performance cookies. Third-party analytics tools may process
              anonymized or pseudonymized data.
            </Typography>
            <Typography variant='body1' paragraph>
              You can adjust your browser settings to refuse cookies or alert you
              when cookies are being used. However, disabling cookies may limit
              certain features of the Platform.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              11. Children&apos;s Privacy
            </Typography>
            <Typography variant='body1' paragraph>
              The Platform is not intended for minors under 18. We do not knowingly
              collect data from minors.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              12. Third-Party Links
            </Typography>
            <Typography variant='body1' paragraph>
              The Platform may contain links to external websites. We are not
              responsible for third-party privacy practices.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              13. Reverse Solicitation Notice
            </Typography>
            <Typography variant='body1' paragraph>
              The Platform does not solicit EU clients, market to US residents, or
              conduct targeted advertising in restricted jurisdictions. Any EU or US
              user who accesses the Platform does so voluntarily and independently.
              GDPR applies solely due to cross-border data flow.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              14. Dispute Resolution
            </Typography>
            <Typography variant='body1' paragraph>
              This Policy is governed by the laws of Panama. Disputes shall be
              resolved by binding arbitration in Panama City, in English.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              15. Changes to This Policy
            </Typography>
            <Typography variant='body1' paragraph>
              We may update this Policy periodically. Continued use of the Platform
              constitutes acceptance of the updated Policy.
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant='h6' gutterBottom>
              16. Contact Information
            </Typography>
            <Typography variant='body1' paragraph>
              For privacy requests: POLAR TENSOR CORP., Compliance Department,
              compliance@polar-tensor.io, Republic of Panama.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <br />
    </Container>
  )
}
